import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Coupons = () => {
  const { authHeader } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', type: 'percentage', value: '', minOrderAmount: '', expiresAt: '', isActive: true, usageLimit: '' });

  useEffect(() => {
    axios.get(`${API}/coupons`, { headers: authHeader }).then(r => setCoupons(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!form.code || !form.value) { toast.error('Code and value required'); return; }
    try {
      const payload = { ...form, value: Number(form.value), minOrderAmount: Number(form.minOrderAmount) || 0, usageLimit: Number(form.usageLimit) || 0 };
      if (editing) {
        const { data } = await axios.put(`${API}/coupons/${editing._id}`, payload, { headers: authHeader });
        setCoupons(prev => prev.map(c => c._id === editing._id ? data : c));
        toast.success('Coupon updated');
      } else {
        const { data } = await axios.post(`${API}/coupons`, payload, { headers: authHeader });
        setCoupons(prev => [...prev, data]);
        toast.success('Coupon created');
      }
      setShowForm(false); setEditing(null); setForm({ code: '', type: 'percentage', value: '', minOrderAmount: '', expiresAt: '', isActive: true, usageLimit: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save coupon');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await axios.delete(`${API}/coupons/${id}`, { headers: authHeader });
      setCoupons(prev => prev.filter(c => c._id !== id));
      toast.success('Coupon deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const startEdit = (c) => {
    setEditing(c); setShowForm(true);
    setForm({ code: c.code, type: c.type, value: c.value, minOrderAmount: c.minOrderAmount || '', expiresAt: c.expiresAt ? c.expiresAt.split('T')[0] : '', isActive: c.isActive, usageLimit: c.usageLimit || '' });
  };

  return (
    <div className="p-6" data-testid="admin-coupons">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-playfair text-2xl font-bold text-[#1F1410]">Coupons</h1>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ code: '', type: 'percentage', value: '', minOrderAmount: '', expiresAt: '', isActive: true, usageLimit: '' }); }}
          className="flex items-center gap-2 bg-[#7A1125] hover:bg-[#8B1A2B] text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors" data-testid="add-coupon-button">
          <Plus size={16} /> Add Coupon
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl" data-testid="coupon-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-playfair font-bold text-[#1F1410]">{editing ? 'Edit' : 'Add'} Coupon</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-[#FDF8F3] rounded-full"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              {[
                { key: 'code', label: 'Coupon Code *', placeholder: 'SAVE10', type: 'text' },
                { key: 'value', label: 'Discount Value *', placeholder: '10', type: 'number' },
                { key: 'minOrderAmount', label: 'Min Order (₹)', placeholder: '200', type: 'number' },
                { key: 'usageLimit', label: 'Usage Limit (0 = unlimited)', placeholder: '0', type: 'number' },
                { key: 'expiresAt', label: 'Expires At', placeholder: '', type: 'date' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">{f.label}</label>
                  <input type={f.type} value={form[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} placeholder={f.placeholder}
                    className="w-full px-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]" data-testid={`coupon-${f.key}`} />
                </div>
              ))}
              <div>
                <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Type</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125] bg-white">
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (₹)</option>
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="rounded" />
                <span className="text-sm text-[#1F1410]">Active</span>
              </label>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-[#ECE0DA] rounded-full text-sm text-[#7A6A63] hover:bg-[#FDF8F3]">Cancel</button>
              <button onClick={handleSave} className="flex-1 py-2.5 bg-[#7A1125] hover:bg-[#8B1A2B] text-white text-sm font-semibold rounded-full" data-testid="save-coupon-button">Save</button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#ECE0DA] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#7A1125] border-t-transparent rounded-full animate-spin" /></div>
        ) : coupons.length === 0 ? (
          <div className="text-center py-12 text-[#7A6A63]">No coupons yet</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-[#FDF8F3] border-b border-[#ECE0DA]">
                {['Code', 'Type', 'Value', 'Min Order', 'Usage', 'Expires', 'Status', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#7A6A63] uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECE0DA]">
              {coupons.map(c => (
                <tr key={c._id} className="hover:bg-[#FDF8F3] transition-colors" data-testid={`coupon-row-${c._id}`}>
                  <td className="px-4 py-3 font-mono font-bold text-[#7A1125]">{c.code}</td>
                  <td className="px-4 py-3 text-[#7A6A63] capitalize">{c.type}</td>
                  <td className="px-4 py-3 font-semibold">{c.type === 'percentage' ? `${c.value}%` : `₹${c.value}`}</td>
                  <td className="px-4 py-3 text-[#7A6A63]">₹{c.minOrderAmount || 0}</td>
                  <td className="px-4 py-3 text-[#7A6A63]">{c.usageCount}/{c.usageLimit || '∞'}</td>
                  <td className="px-4 py-3 text-[#7A6A63] text-xs">{c.expiresAt ? new Date(c.expiresAt).toLocaleDateString('en-IN') : '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.isActive ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-500 border'}`}>
                      {c.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => startEdit(c)} className="p-1.5 hover:bg-[#FDF8F3] rounded-lg text-[#7A6A63] hover:text-[#7A1125] transition-colors" data-testid={`edit-coupon-${c._id}`}><Edit size={14} /></button>
                      <button onClick={() => handleDelete(c._id)} className="p-1.5 hover:bg-red-50 rounded-lg text-[#7A6A63] hover:text-red-500 transition-colors" data-testid={`delete-coupon-${c._id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Coupons;
